import { useQuery } from "@tanstack/react-query";
import type { UserInfoResponse } from "../services/apiService";
import { useApiService } from "../contexts/ApiServiceContext";
import { useOptionalWidgetAPI } from "../widget/hooks/useWidgetAPI";
import { isInTransientOutage, transientErrorRefetchInterval } from "../utils/transientErrorRefetch";

/** The one cache key of the `me` query (Header and Dashboard read the same entry). */
export const CURRENT_USER_QUERY_KEY = ["me"] as const;

/**
 * The signed-in user from /api/auth/me, shared by the Header (name) and the Dashboard
 * (token facility ids, client id).
 *
 * One query for every reader: one /api/auth/me request per token and one host onError
 * per 401 (apiService reports it). No retry, and nothing else marks it stale; when the
 * host pushes a new token, WidgetProvider invalidates every query, so a `me` that failed
 * with the expired token is asked again with the new one. After a 5xx or a network
 * failure it asks again on its own, bounded (transientErrorRefetchInterval), so the
 * Header name and the Dashboard facility list recover without a reload; the host hears
 * about each outage window once (the repeats pass reportServerError: false).
 *
 * The hook picks the API client itself, so every reader runs the same request whichever
 * reader rendered last (React Query uses the queryFn of that reader): in a widget, a
 * client built from the widget config (WorkQueue base URL, host token, host onError);
 * outside a widget, the client from ApiServiceContext (the one the Dashboard provides).
 */
export function useCurrentUser() {
  const widgetApi = useOptionalWidgetAPI();
  const contextApi = useApiService();
  const api = widgetApi ?? contextApi;

  return useQuery<UserInfoResponse>({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: async ({ client, queryKey }) => {
      try {
        return await api.getCurrentUser({ reportServerError: !isInTransientOutage(client, queryKey) });
      } catch (error) {
        // Readers keep their fallback (the Header name, the token facility list); the
        // error is reported to the host by apiService.
        if (import.meta.env.DEV) {
          console.warn("[useCurrentUser] Failed to fetch user info:", error);
        }
        throw error;
      }
    },
    retry: false,
    staleTime: Infinity,
    refetchInterval: (query) => transientErrorRefetchInterval(query, false),
  });
}
